import { CmsCard } from '../../cms/CmsCard';
import { SelectOptionDialog } from '../../option/SelectOptionDialog';
import { getEpgWatchability } from '../../../Business/detail/epg/EpgWatchability';
import { DetailTranslationKeys } from '../../../api/models/TranslationKeys';
import { authShowCancelRecord, authShowDeleteRecord, authShowStartRecord } from './MiniEpgHelperFunctions';
import { ScheduleDetailDataSource } from '../../../Business/detail/epg/ScheduleDetailDataSource';
import { CardInfo } from '../../../Business/cms/CardInfo';
import { Log } from '@lightningjs/sdk';
import { DetailPage } from '../../../pages/detail/DetailPage';

const WatchOptions = {
  replay: 'replay',
  replayFromBookmark: 'replayFromBookmark',
  live: 'live',
  recording: 'recording',
  recordingFromBookmark: 'recordingFromBookmark',
};

const optionTexts = {
  [WatchOptions.replay]: DetailTranslationKeys.detail_watch_from_beginning,
  [WatchOptions.replayFromBookmark]: DetailTranslationKeys.detail_watch_from_bookmark,
  [WatchOptions.live]: DetailTranslationKeys.detail_watch_live,
  [WatchOptions.recording]: DetailTranslationKeys.detail_watch_from_beginning,
  [WatchOptions.recordingFromBookmark]: DetailTranslationKeys.detail_watch_from_bookmark,
};

export class MiniEpgCard extends CmsCard {
  /**
   * @type {CardDataSource}
   */
  data;
  _handleUp() {
    return false;
  }

  _firstActive() {
    this.info = new CardInfo(this.data, true);
    return super._firstActive();
  }

  _focus() {
    super._focus();
    this.fireAncestors('$startOverlayTimeout');
  }

  _handleEnter() {
    // console.log(this.data);
    this.fireAncestors('$stopOverlayTimeout');
    this.detailDataSource = new ScheduleDetailDataSource(this.data.backend);
    this.detailDataSourcePromise = this.detailDataSource.load(this.data.extractor.schedule.id).then(() => {
      const epgWatchAction = this.detailDataSource.getEpgWatchAction();
      epgWatchAction.getOptions().then(watchOptions => {
        this.fireAncestors('$openDialog', {
          type: SelectOptionDialog,
          params: {
            backend: this.data.backend,
            title: this.data.asset.asset.title,
            options: this.getOptions(watchOptions),
            onSuccess: this.onOptionSelect.bind(this),
            onCancelled: () => {
              Log.info('Mini epg cancelled');
            },
          },
        });
      });
    });
  }

  onOptionSelect(val) {
    Log.info('optionSelect', val);
    this.fireAncestors('$closeDialog');
    switch (val.value) {
      case 'WATCH':
        Log.info('do watch action');
        this.doWatchNow();
        break;
      case 'RECORD':
        Log.info('do recording action');
        this.doRecording();
        break;
      case 'CANCEL_RECORDING':
        Log.info('do cancel recording action');
        this.doCancelRecording();
        break;
      case 'DELETE_RECORDING':
        Log.info('do delete recording action');
        this.doDeleteRecording();
        break;
      case 'DETAIL':
        Log.info('do detail action');
        this.doDetail(); // open detail page
        break;
      default:
        this.doWatchNow(val);
    }
  }

  authShowWatchButton() {
    return getEpgWatchability(
      this.data.backend,
      this.data.extractor.schedule,
      this.data.extractor.channel,
      this.data.recording,
      false
    ).showWatchButton;
  }

  getOptions(epgWatchOptions) {
    const options = [];
    epgWatchOptions.forEach(o => {
      options.push({ text: this.data.backend.translations.forKey(optionTexts[o.option]), value: o });
    });

    // if (this.authShowWatchButton()) options.push(this.getWatchOption());
    if (
      authShowStartRecord(
        this.data.backend,
        this.data.extractor.schedule,
        this.data.extractor.channel,
        this.data.recording
      )
    ) {
      options.push(this.getRecordOption());
    } else if (authShowCancelRecord(this.data.recording)) {
      options.push(this.getCancelRecordOption());
    } else if (authShowDeleteRecord(this.data.recording)) {
      options.push(this.getDeleteRecordOption());
    }
    options.push(this.getDetailOption());
    return options;
  }

  getDetailOption() {
    return {
      text: this.data.backend.translations.forKey(DetailTranslationKeys.detail_btn_show_details),
      value: 'DETAIL',
    };
  }

  getWatchOption() {
    return {
      text: this.data.backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
      value: 'WATCH',
    };
  }

  getRecordOption() {
    return {
      text: this.data.backend.translations.forKey(DetailTranslationKeys.detail_recording_start),
      value: 'RECORD',
    };
  }

  getCancelRecordOption() {
    return {
      text: this.data.backend.translations.forKey(DetailTranslationKeys.detail_recording_cancel),
      value: 'CANCEL_RECORDING',
    };
  }

  getDeleteRecordOption() {
    return {
      text: this.data.backend.translations.forKey(DetailTranslationKeys.detail_recording_delete),
      value: 'DELETE_RECORDING',
    };
  }

  doWatchNow(selectedOption) {
    if (this.detailDataSourcePromise) {
      this.detailDataSourcePromise.then(() => {
        const action = this.detailDataSource.getEpgWatchAction();
        action.owner = this;
        action.optionSelected(selectedOption);
      });
    }
  }

  doRecording() {
    if (this.detailDataSourcePromise) {
      this.detailDataSourcePromise.then(() => {
        const action = this.detailDataSource.getRecordingAction();
        if (action) {
          action.owner = this;
          action.doAction();
        } else {
          Log.info('Recording action could not be created');
        }
      });
    }
  }

  doCancelRecording() {
    if (this.detailDataSourcePromise) {
      this.detailDataSourcePromise.then(() => {
        const action = this.detailDataSource.getCancelRecordAction();
        if (action) {
          action.owner = this;
          action.doAction();
        } else {
          Log.info('Recording action could not be created');
        }
      });
    }
  }

  doDeleteRecording() {
    if (this.detailDataSourcePromise) {
      this.detailDataSourcePromise.then(() => {
        const action = this.detailDataSource.getDeleteRecordAction();
        action.owner = this;
        action.doAction();
      });
    }
  }

  doDetail() {
    if (this.detailDataSourcePromise) {
      this.detailDataSourcePromise.then(() => {
        const action = this.data.action;
        this.fireAncestors('$openPlayerModal', DetailPage, {
          backend: this.data.backend,
          assetType: action.type,
          assetId: action.id,
        });
      });
    }
  }
}
